// AI Writing Assistant - Floating panel with writing enhancement options
import { useState } from 'react';
import { DOMSerializer } from 'prosemirror-model';
import { useAI } from '../../contexts/AIContext.jsx';
import { continueWriting, improveWriting, expandIdeas } from '../../services/ai-config.js';
import './AIAssistant.css';

export default function AIAssistant({ editor }) {
    const { aiEnabled, aiConfigured, customPrompts } = useAI();
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [activeAction, setActiveAction] = useState(null);
    const [error, setError] = useState(null);
    const [showInput, setShowInput] = useState(null); // 'continue' | 'expand' | null
    const [userDirection, setUserDirection] = useState('');

    if (!aiEnabled || !aiConfigured || !editor) {
        return null;
    }

    // Get full editor HTML content (preserves formatting)
    const getEditorHTML = () => {
        return editor.getHTML();
    };

    // Get selected HTML content using ProseMirror DOMSerializer
    const getSelectedHTML = () => {
        const { from, to } = editor.state.selection;
        if (from === to) return '';

        try {
            const slice = editor.state.doc.slice(from, to);
            const serializer = DOMSerializer.fromSchema(editor.schema);
            const fragment = serializer.serializeFragment(slice.content);
            const tempDiv = document.createElement('div');
            tempDiv.appendChild(fragment);
            return tempDiv.innerHTML;
        } catch (e) {
            console.warn('HTML extraction failed, using text:', e);
            return editor.state.doc.textBetween(from, to, '\n');
        }
    };

    // Get plain text for last paragraph detection
    const getLastParagraphText = () => {
        const text = editor.getText();
        const paragraphs = text.split('\n').filter(p => p.trim());
        return paragraphs[paragraphs.length - 1] || '';
    };

    // Insert HTML content (preserves all formatting)
    const insertHTML = (html) => {
        editor.chain().focus().insertContent(html, {
            parseOptions: { preserveWhitespace: true }
        }).run();
    };

    // Replace selection with HTML content
    const replaceSelectionHTML = (html) => {
        editor.chain().focus().deleteSelection().insertContent(html, {
            parseOptions: { preserveWhitespace: true }
        }).run();
    };

    const handleContinue = async () => {
        const fullHTML = getEditorHTML();
        const lastParagraph = getLastParagraphText();

        if (!lastParagraph || lastParagraph.length < 20) {
            setError('Please write more content first.');
            return;
        }

        setIsLoading(true);
        setActiveAction('continue');
        setError(null);

        try {
            const continuation = await continueWriting(
                fullHTML,
                lastParagraph,
                customPrompts.continue,
                userDirection || null
            );
            editor.commands.focus('end');
            insertHTML(' ' + continuation);
            setIsOpen(false);
            setShowInput(null);
            setUserDirection('');
        } catch (err) {
            setError(err.message || 'Failed to continue writing.');
        } finally {
            setIsLoading(false);
            setActiveAction(null);
        }
    };

    const handleImprove = async () => {
        const selectedHTML = getSelectedHTML();

        if (!selectedHTML || selectedHTML.length < 10) {
            setError('Please select some text to improve.');
            return;
        }

        setIsLoading(true);
        setActiveAction('improve');
        setError(null);

        try {
            const improved = await improveWriting(selectedHTML, customPrompts.improve);
            replaceSelectionHTML(improved);
            setIsOpen(false);
        } catch (err) {
            setError(err.message || 'Failed to improve writing.');
        } finally {
            setIsLoading(false);
            setActiveAction(null);
        }
    };

    const handleExpand = async () => {
        const selectedHTML = getSelectedHTML();
        const fullHTML = getEditorHTML();

        if (!selectedHTML || selectedHTML.length < 10) {
            setError('Please select some text to expand.');
            return;
        }

        setIsLoading(true);
        setActiveAction('expand');
        setError(null);

        try {
            const expanded = await expandIdeas(
                selectedHTML,
                fullHTML,
                customPrompts.expand,
                userDirection || null
            );
            replaceSelectionHTML(expanded);
            setIsOpen(false);
            setShowInput(null);
            setUserDirection('');
        } catch (err) {
            setError(err.message || 'Failed to expand ideas.');
        } finally {
            setIsLoading(false);
            setActiveAction(null);
        }
    };

    const togglePanel = () => {
        setIsOpen(!isOpen);
        setError(null);
        setShowInput(null);
        setUserDirection('');
    };

    const handleActionClick = (action) => {
        if (action === 'improve') {
            handleImprove();
        } else if (action === 'continue' || action === 'expand') {
            // Show input for direction
            if (showInput === action) {
                // Execute with current direction
                action === 'continue' ? handleContinue() : handleExpand();
            } else {
                setShowInput(action);
                setUserDirection('');
            }
        }
    };

    return (
        <div className="ai-assistant">
            {/* Floating button - modern sparkle icon */}
            <button
                className={`ai-assistant-trigger ${isOpen ? 'active' : ''}`}
                onClick={togglePanel}
                title="AI Writing Assistant"
            >
                <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                    <path d="M12 2L13.09 8.26L19 7L14.74 11.09L21 12L14.74 12.91L19 17L13.09 15.74L12 22L10.91 15.74L5 17L9.26 12.91L3 12L9.26 11.09L5 7L10.91 8.26L12 2Z" />
                </svg>
            </button>

            {/* Panel */}
            {isOpen && (
                <div className="ai-assistant-panel">
                    <div className="ai-assistant-header">
                        <span>AI Writing Assistant</span>
                        <button className="ai-assistant-close" onClick={() => setIsOpen(false)}>×</button>
                    </div>

                    {error && (
                        <div className="ai-assistant-error">{error}</div>
                    )}

                    <div className="ai-assistant-actions">
                        <button
                            className={`ai-action-btn ${showInput === 'continue' ? 'active' : ''}`}
                            onClick={() => handleActionClick('continue')}
                            disabled={isLoading}
                        >
                            <span className="ai-action-icon">✍️</span>
                            <div className="ai-action-info">
                                <span className="ai-action-name">Continue Writing</span>
                                <span className="ai-action-desc">AI continues from where you left off</span>
                            </div>
                            {isLoading && activeAction === 'continue' && (
                                <span className="ai-action-loading" />
                            )}
                        </button>

                        {showInput === 'continue' && (
                            <div className="ai-action-input">
                                <input
                                    type="text"
                                    placeholder="What should I write about? (optional)"
                                    value={userDirection}
                                    onChange={(e) => setUserDirection(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleContinue()}
                                    autoFocus
                                />
                                <button onClick={handleContinue} disabled={isLoading}>
                                    {isLoading ? '...' : 'Go'}
                                </button>
                            </div>
                        )}

                        <button
                            className="ai-action-btn"
                            onClick={() => handleActionClick('improve')}
                            disabled={isLoading}
                        >
                            <span className="ai-action-icon">✨</span>
                            <div className="ai-action-info">
                                <span className="ai-action-name">Improve & Restructure</span>
                                <span className="ai-action-desc">Clean up and structure selected content</span>
                            </div>
                            {isLoading && activeAction === 'improve' && (
                                <span className="ai-action-loading" />
                            )}
                        </button>

                        <button
                            className={`ai-action-btn ${showInput === 'expand' ? 'active' : ''}`}
                            onClick={() => handleActionClick('expand')}
                            disabled={isLoading}
                        >
                            <span className="ai-action-icon">📝</span>
                            <div className="ai-action-info">
                                <span className="ai-action-name">Expand Ideas</span>
                                <span className="ai-action-desc">Add more details to selected text</span>
                            </div>
                            {isLoading && activeAction === 'expand' && (
                                <span className="ai-action-loading" />
                            )}
                        </button>

                        {showInput === 'expand' && (
                            <div className="ai-action-input">
                                <input
                                    type="text"
                                    placeholder="What aspect to expand? (optional)"
                                    value={userDirection}
                                    onChange={(e) => setUserDirection(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleExpand()}
                                    autoFocus
                                />
                                <button onClick={handleExpand} disabled={isLoading}>
                                    {isLoading ? '...' : 'Go'}
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="ai-assistant-tip">
                        💡 Select text first for Improve and Expand features
                    </div>
                </div>
            )}
        </div>
    );
}

